// IMP-01 (impedance) — README.md. Applies to passive speakers only; the
// driving amp (AV unit or external power amp) comes from `ctx.assignment`.

import type { BuildContext } from '../context';
import type { Issue, RuleId } from '../types';
import { productName, issueId, fmtOhms } from '../copy';

interface Group {
  speakerIds: string[];
  productId: string;
  z: number;
  ampName: string;
  min: number;
}

export function checkImp01(ctx: BuildContext): { issues: Issue[]; unknown: { rule: RuleId; productIds: string[] }[] } {
  if (!ctx.assignment || !ctx.avUnit) return { issues: [], unknown: [] };

  // Group by (speaker product, driving amp) so several speakers sharing
  // both give one issue — README "Several speakers with the same product
  // and amp give one issue."
  const groups = new Map<string, Group>();
  const unknownProductIds = new Set<string>();

  for (const [speakerId, a] of ctx.assignment.bySpeaker) {
    if (a.active || !a.driving || a.driving.kind === 'preout') continue;
    const product = a.product;
    if (!product || product.specs.kind !== 'speaker') continue; // no bound product — not applicable

    let min: number | undefined;
    let ampName: string;
    let ampKey: string;
    let ampProductId: string;
    if (a.driving.kind === 'internal') {
      min = ctx.avUnit.specs.minImpedanceOhm?.v;
      ampName = productName(ctx.avUnit.product);
      ampKey = 'av';
      ampProductId = ctx.avUnit.product.id;
    } else {
      const gearId = a.driving.gearId;
      const ampGear = ctx.amps.find((x) => x.gearId === gearId);
      if (!ampGear) continue;
      min = ampGear.specs.minImpedanceOhm.v;
      ampName = productName(ampGear.product);
      ampKey = `amp:${ampGear.gearId}`;
      ampProductId = ampGear.product.id;
    }

    const impedanceOhm = product.specs.impedanceOhm;
    if (!impedanceOhm) {
      // Speaker's bound product lacks impedanceOhm — README "a rule that
      // needs a field a bound product lacks adds unknown".
      unknownProductIds.add(product.id);
      continue;
    }
    if (min == null) {
      // Driving amp's bound product lacks minImpedanceOhm.
      unknownProductIds.add(ampProductId);
      continue;
    }
    if (impedanceOhm.v >= min) continue;

    const key = `${product.id}|${ampKey}`;
    const g = groups.get(key) ?? { speakerIds: [], productId: product.id, z: impedanceOhm.v, ampName, min };
    g.speakerIds.push(speakerId);
    groups.set(key, g);
  }

  const issues: Issue[] = [];
  for (const g of groups.values()) {
    const productObj = ctx.catalog.get(g.productId);
    if (!productObj) continue;
    issues.push({
      id: issueId('IMP-01', g.speakerIds),
      rule: 'IMP-01',
      severity: 'error',
      text: `${productName(productObj)} is ${fmtOhms(g.z)} Ω; ${g.ampName} supports ${fmtOhms(g.min)} Ω and up.`,
      fix: `Drive it with an amplifier rated for ${fmtOhms(g.z)} Ω.`,
      subjects: g.speakerIds,
    });
  }

  const unknown = unknownProductIds.size > 0
    ? [{ rule: 'IMP-01' as RuleId, productIds: [...unknownProductIds].sort() }]
    : [];
  return { issues, unknown };
}
